// admin/src/services/api.js
import axios from "axios";

/* ------------------------------------------------------------------ */
/* Environment configuration (CRA)                                     */
/* CRA only exposes env vars prefixed with REACT_APP_                  */
/* ------------------------------------------------------------------ */
const IS_DEV = process.env.NODE_ENV === "development";

// Dev fallback so local development works without a .env file.
// Production has no fallback — it MUST be set at build time.
const API_URL =
  process.env.REACT_APP_API_URL ||
  (IS_DEV ? "http://localhost:8000/api" : null);

const API_TIMEOUT_MS =
  parseInt(process.env.REACT_APP_API_TIMEOUT_MS, 10) || 15000;

/* Fail fast if the API URL is missing or malformed. */
if (!API_URL) {
  throw new Error(
    "[api.js] REACT_APP_API_URL is not set. Add it to your admin/.env file. " +
      "Example: REACT_APP_API_URL=https://api.arkila.com/api"
  );
}

const isValidUrl = (str) => {
  try {
    const u = new URL(str);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

if (!isValidUrl(API_URL)) {
  throw new Error(`[api.js] REACT_APP_API_URL is not a valid URL: ${API_URL}`);
}

const API_BASE = API_URL.replace(/\/+$/, "");
const ROOT_BASE = API_BASE.replace(/\/api\/?$/, "");

/* ------------------------------------------------------------------ */
/* Axios instance                                                      */
/* ------------------------------------------------------------------ */
const api = axios.create({
  baseURL: API_BASE,
  timeout: API_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // You use Bearer tokens (not Sanctum cookies).
  // Keep this false to avoid CORS preflight complications.
  withCredentials: false,
});

/* ------------------------------------------------------------------ */
/* Endpoints that should NEVER trigger the global 401 redirect.        */
/* The login page handles these errors inline (SweetAlert).            */
/* ------------------------------------------------------------------ */
const AUTH_ENDPOINTS_NO_REDIRECT = [
  "/admin/request-login-otp",
  "/admin/verify-login-otp",
  "/admin/login",
];

const isAuthEndpoint = (url = "") =>
  AUTH_ENDPOINTS_NO_REDIRECT.some((endpoint) => url.includes(endpoint));

/* ------------------------------------------------------------------ */
/* Request interceptor                                                 */
/* ------------------------------------------------------------------ */
api.interceptors.request.use(
  (config) => {
    if (config.url === "/sanctum/csrf-cookie") {
      config.baseURL = ROOT_BASE;
      delete config.headers.Authorization;
      return config;
    }

    const token = localStorage.getItem("arkila_admin_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Only add ngrok header when actually hitting a tunnel
    try {
      const host = new URL(config.baseURL || API_BASE).hostname;
      if (
        host.endsWith(".ngrok.io") ||
        host.endsWith(".ngrok-free.app") ||
        host.endsWith(".trycloudflare.com")
      ) {
        config.headers["ngrok-skip-browser-warning"] = "true";
      }
    } catch {
      /* ignore */
    }

    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.debug(`→ ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ------------------------------------------------------------------ */
/* Response interceptor                                                */
/* ------------------------------------------------------------------ */
let hasRedirectedToLogin = false;

api.interceptors.response.use(
  (response) => {
    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.debug(
        `← ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`
      );
    }
    return response;
  },
  (error) => {
    if (axios.isCancel?.(error) || error.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const url = error.config?.url;
    const method = (error.config?.method || "GET").toUpperCase();

    if (IS_DEV) {
      // eslint-disable-next-line no-console
      console.error(`✖ ${status || "NET"} ${method} ${url}`, {
        message: error.message,
        data: error.response?.data,
      });
    }

    /* ────────────────────────────────────────────────────────────
       401 handling:
       - Skip entirely for login/OTP endpoints → let the Login page
         display the error inline (SweetAlert).
       - Otherwise → clear the token and redirect to /login with a
         hint so the user sees a "session expired" notice, and a
         `from` param so they land back where they were after
         signing in again.
    ──────────────────────────────────────────────────────────── */
    if (status === 401 && !isAuthEndpoint(url) && !hasRedirectedToLogin) {
      hasRedirectedToLogin = true;
      localStorage.removeItem("arkila_admin_token");
      localStorage.removeItem("arkila_admin_data");

      if (window.location.pathname !== "/login") {
        const currentPath =
          window.location.pathname + window.location.search;
        const loginUrl = `/login?expired=1&from=${encodeURIComponent(
          currentPath
        )}`;
        // Hard redirect wipes React state cleanly (no half-rendered views)
        window.location.assign(loginUrl);
      }

      // Reset the flag after a short delay so future 401s can redirect again
      setTimeout(() => {
        hasRedirectedToLogin = false;
      }, 3000);
    }

    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ */
/* CSRF cookie (Sanctum)                                               */
/* ------------------------------------------------------------------ */
export const getCsrfCookie = async (retries = 3) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await api.get("/sanctum/csrf-cookie");
    } catch (error) {
      const status = error.response?.status;
      if (status && status < 500 && status !== 0) throw error;
      if (attempt === retries) throw error;

      const delay = Math.pow(2, attempt - 1) * 500;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
};

/* ------------------------------------------------------------------ */
/* Retry helper                                                        */
/* ------------------------------------------------------------------ */
const RETRYABLE_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

export const makeRequest = async (requestFn, retries = 2) => {
  let lastError;
  for (let attempt = 1; attempt <= retries + 1; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      if (
        error.code === "ERR_CANCELED" ||
        [400, 401, 403, 404, 409, 422].includes(error.response?.status)
      ) {
        throw error;
      }

      const status = error.response?.status;
      const isNetworkError = !error.response;
      const isRetryable = isNetworkError || RETRYABLE_STATUSES.has(status);

      if (!isRetryable || attempt > retries) throw error;

      const delay = Math.pow(2, attempt - 1) * 500;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastError;
};

export const apiWithRetry = {
  get: (url, config) => makeRequest(() => api.get(url, config)),
  post: (url, data, config) => api.post(url, data, config),
  put: (url, data, config) => api.put(url, data, config),
  patch: (url, data, config) => api.patch(url, data, config),
  delete: (url, config) => api.delete(url, config),
};

/* ------------------------------------------------------------------ */
/* Utilities                                                           */
/* ------------------------------------------------------------------ */
export const checkApiHealth = async () => {
  try {
    const response = await api.get("/health", { timeout: 5000 });
    return response.status === 200;
  } catch {
    return false;
  }
};

export const uploadFile = async (url, file, onProgress) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const pct = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(pct);
      }
    },
  });
  return response;
};

export default api;